import { ref } from 'vue'
import axios from 'axios'
import { FileApi } from '../test-api/index.ts'
import PromiseQueue from '../promiseQueue'
import { useToast } from 'vue-toastification'
import UploadNotification from '../components/Flashes/UploadNotification.vue'
import AlertNotification from '../components/Flashes/AlertNotification.vue'
// import { reactive } from 'vue'
import { $t } from 'nette-base-module/js/vuei18next'
import useBcUploaderStore from '@/frontapp/store/bcUploader'
import handleApiError from '../utils/handleApiError'

const fileApi = new FileApi({}, '/api/v2')
const CancelToken = axios.CancelToken

let bcUploaderStore = null

const getBcUploaderStore = () => {
  if (bcUploaderStore) {
    return bcUploaderStore
  }

  return bcUploaderStore = useBcUploaderStore()
}

const defaults = {
  name: null,
  uploaderId: null,
  maxUploadFiles: null, // unlimited
  from: 'user', // file input name
  documentId: null, // upload from document id
  folderId: null, // upload from folder id
  exts: null, // allowed file extensions
  highlightClass: 'upload-hightlight',
  highlightErrorClass: 'upload-hightlight-error',
  click: true, // handle element clicks?
  highlightTimeout: 0,
  onUploadStart (/*uploaderId*/) {
  },
  onFileUploadDone (/*data, axiosResponse*/) {
    //window.alert('You must set onFileUploadDone callback')
  },
  onError (error, uploaderId) {
    console.log('uppload error', error)
    const toast = useToast()

    toast({
      component: AlertNotification,
      props:  {
        alert: 'error',
        heading: error,
        uploaderId,
      },
      listeners: {}
    }, {
      id: uploaderId,
      type: 'error',
    })
  },
  onProgress (progress, percentCompleted) {
    console.log('uppload progress', progress, percentCompleted)
  },
  onAllFilesUploadDone (/*data, axiosResponse*/) {
    //window.alert('You must set onFileUploadDone callback')
  },
  onUploadStop (/*uploaderId*/) {
  },
}

const errors = {
  maxUploadFiles: 'Max uploaded files limit exceeded. You can upload maximum %maxUploadFiles% files',
  exts: 'File extensions %ext% is not allowed. Allowed extensions are %allowed%.',
  upload: 'File upload failed'
}

const $http = axios.create({
  headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'multipart/form-data' }
})

const checkUploaded = async function (id, multipart_upload_id, etags, attempt) {
  try {
    const uploadedFileResponse = await handleApiError(() => fileApi.fileUploaded(id, { multipart_upload_id, etags }))
    return uploadedFileResponse.data
  } catch (error) {
    if (attempt <= 3) {
      return await checkUploaded(id, multipart_upload_id, etags, ++attempt)
    } else {
      throw error
    }
  }
};

const uploadFile = async (file, { from, documentId, folderId, onError }, uploaderId, uploaderFileIndex) => {
  getBcUploaderStore().startFileUpload(uploaderId, uploaderFileIndex)
  const uploadRequestResponse = await handleApiError(() => fileApi.fileRequestUpload({
    name: file.name,
    size: file.size,
    from,
    bc_document_id: documentId,
    bc_folder_id: folderId,
  }))

  const queue = new PromiseQueue({
    concurrent: 1,
    interval: 100,
    start: false,
  })


  uploadRequestResponse.data.upload_url.forEach(upload_url => {
    const part = {
      url: upload_url.upload_url,
      cancelToken: undefined,
      start: () => { // callback to start upload
        const blob = file.slice(upload_url.start, upload_url.end)
        part.shouldRunning.value = true
        part.cancelToken = CancelToken.source()
        part.isPaused.value = false

        $http.put(upload_url.upload_url, blob, {
          onUploadProgress(progressEvent) {
            part.loaded.value = progressEvent.loaded
          },
          cancelToken: part.cancelToken.token
        }).then((uploadDoneData) => {
          part.loaded.value = upload_url.end - upload_url.start
          part.uploadDonePromise.resolve(uploadDoneData)
          part.shouldRunning.value = false
          part.etag = uploadDoneData.headers.etag.replaceAll('"', '')
          console.log('upload part of file done', uploadDoneData)
        }, (error) => {
          // nic nedela
          if (part.cancelToken !== undefined) {
            onError(error)
          }
        })
      },
      pause: () => { // callback to pause upload,
        if (part.cancelToken !== undefined) {
          part.cancelToken.cancel()
          part.cancelToken = undefined
          part.isPaused.value = true
        }
      },
      isPaused: ref(false),
      loaded: ref(0),
      shouldRunning: ref(false),
      uploadDonePromise: null,
      etag: null, // returned etag after correct part upload
    }

    getBcUploaderStore().addFileUploadPart(uploaderId, uploaderFileIndex, part)

    queue.add(() => {
      return new Promise((resolve, reject) => {
        part.uploadDonePromise = { resolve, reject }
        part.start()
      })
    })
  })

  await queue.start()
  return getBcUploaderStore().currentUploadedFile(uploaderId).bcFile = await checkUploaded(
    uploadRequestResponse.data.id,
    uploadRequestResponse.data.multipart_upload_id,
    getBcUploaderStore().listCurrentUploadedFilePartsEtags(uploaderId)
  )
}

/*
const previewFile = (file) => {
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onloadend = function () {
    const img = document.createElement('img')
    img.src = reader.result
    document.getElementById('gallery').appendChild(img)
  }
}
*/

const createForm = (el, { maxUploadFiles }) => {
  const form = document.createElement('FORM')
  form.style.display = 'none'
  el.appendChild(form)

  const input = document.createElement('INPUT')
  input.type = 'file'

  if (maxUploadFiles !== 1) {
    input.setAttribute('multiple', 'multiple')
  }

  form.appendChild(input)

  return input
}

const checkFiles = (files, { maxUploadFiles, exts }) => {
  // check max uploaded files count
  if (maxUploadFiles !== null && files.length > maxUploadFiles) {
    return $t(errors.maxUploadFiles, { maxUploadFiles })
  }

  // check file exts
  let fileExtError = false
  files.forEach(file => {
    const fileExt = file.name.split('.').pop().toLowerCase()
    if (exts && exts.indexOf(fileExt) === -1) {
      fileExtError = fileExt
    }
  })

  if (fileExtError) {
    return $t(errors.exts, {
      ext: fileExtError,
      allowed: exts.join(', ') 
    })
  }

  return true
}

const handleFiles = (files, config, uploaderId) => {
  if (files.length === 0) {
    return
  }
  
  const check = checkFiles(files, config)
  if (check !== true) {
    config.onError(check, uploaderId)
    return
  }

  const uploaderData = getBcUploaderStore().getUploaderData(uploaderId)
  uploaderData.isRunning = true

  const uploaderQueue = getBcUploaderStore().getUploaderQueue(uploaderId)

  files.forEach(file => {
    const uploaderFileIndex = getBcUploaderStore().addFileToUpload(uploaderId, file)
    uploaderQueue.add(async () => {
      const fileData = await uploadFile(file, config, uploaderId, uploaderFileIndex)
      try {
        await config.onFileUploadDone(fileData)
      } catch (error) {
        config.onError(error, uploaderId)
      }
    })
  })

  const toast = useToast()

  let uploaderStopped = false
  
  const toastContent = {
    component: UploadNotification,
    props:  {
      uploaderId
    },
    listeners: {
      onStop() {
        uploaderStopped = true
        getBcUploaderStore().stopUpload(uploaderId)
        toast.update(uploaderId, { content: toastContent, options: { timeout: 5000, draggable: true, closeOnClick: true } });
        config.onUploadStop(uploaderId)
      },
      onPause() {
        getBcUploaderStore().pauseUpload(uploaderId)
      },
      onResume() {
        getBcUploaderStore().resumeUpload(uploaderId)
      }
    }
  }

  toast(toastContent, {
    id: uploaderId,
    timeout: false,
    draggable: false,
    closeOnClick: false,
  })
  
  if (uploaderQueue.state.value !== 1) {
    config.onUploadStart(uploaderId)

    uploaderQueue.start().then(async () => {
      if (uploaderStopped) {
        return
      }

      toast.update(uploaderId, { content: toastContent, options: { timeout: 5000, draggable: true, closeOnClick: true } });
      await config.onAllFilesUploadDone(getBcUploaderStore().listUploadedFiles(uploaderId), uploaderId)
      uploaderData.isRunning = false
      getBcUploaderStore().resetUploaderFiles(uploaderId)

      if (!uploaderData.isMounted) {
        getBcUploaderStore().unmountUploader(uploaderId)
      }
    })
  }
}

const init = (config, el) => {
  el.awVueUploader = {
    highlight(e) {
      if (typeof config.onFileUploadDone !== 'function' || !e.dataTransfer.items || e.dataTransfer.items.length === 0 || e.dataTransfer.items[0].kind !== 'file') {
        return
      }

      if (el.awVueUploader.willUnhighlight) {
        clearTimeout(el.awVueUploader.willUnhighlight)
        el.awVueUploader.willUnhighlight = null
      }

      if (el.awVueUploader.highlighted !== false || el.awVueUploader.willHighlight) {
        return
      }

      // disable highlight on parent uploaders
      let parent = el.parentNode
      while (parent.tagName !== 'BODY') {
        if (parent.awVueUploader && (parent.awVueUploader.highlighted || parent.awVueUploader.willHighlight)) {
          clearTimeout(parent.awVueUploader.willHighlight)
          parent.awVueUploader.willHighlight = null
          parent.awVueUploader.highlighted = false
          parent.classList.remove(parent.awVueUploader.config.highlightClass)
          parent.classList.remove(parent.awVueUploader.config.highlightErrorClass)
        }
        parent = parent.parentNode
      }

      el.awVueUploader.willHighlight = setTimeout(() => {
          el.awVueUploader.highlighted = true
          if (checkFiles([...e.dataTransfer.files], config) === true) {
            el.classList.add(config.highlightClass)
          } else {
            el.classList.add(config.highlightErrorClass)
          }
      }, config.highlightTimeout);
    },
    unhighlight(e) {
      if (typeof config.onFileUploadDone !== 'function' || !e.dataTransfer.items || e.dataTransfer.items.length === 0 || e.dataTransfer.items[0].kind !== 'file') {
        return
      }
      
      if (el.awVueUploader.willHighlight) {
        clearTimeout(el.awVueUploader.willHighlight)
        el.awVueUploader.willHighlight = null
      }

      if (el.awVueUploader.highlighted === false || el.awVueUploader.willUnhighlight) {
        return
      }
      
      el.awVueUploader.willUnhighlight = setTimeout(() => {
        el.awVueUploader.highlighted = false
        el.classList.remove(config.highlightClass)
        el.classList.remove(config.highlightErrorClass)
      }, config.highlightTimeout)
    },
    preventDefaults(e) {
      e.preventDefault()
      e.stopPropagation()
    },
    handleDrop(e) {
      if (typeof config.onFileUploadDone === 'function' && e.dataTransfer.files && e.dataTransfer.files.length) {
        handleFiles([...e.dataTransfer.files], config, el.awUploaderId)
      }
    },
    handleFile() {
      handleFiles([...el.awVueUploader.fileInput.files], config, el.awUploaderId)
    },
    handleClick() {
      if (typeof config.onFileUploadDone !== 'function') {
        return
      }

      el.awVueUploader.fileInput.click()
    },
    fileInput: createForm(el, config),
    highlighted: false,
    config,
  }

  ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
    el.addEventListener(eventName, el.awVueUploader.preventDefaults, false)
  })

  ;['dragenter', 'dragover'].forEach(eventName => {
    el.addEventListener(eventName, el.awVueUploader.highlight, false)
  })

  ;['dragleave', 'drop'].forEach(eventName => {
    el.addEventListener(eventName, el.awVueUploader.unhighlight, false)
  })

  el.addEventListener('drop', el.awVueUploader.handleDrop, false)

  if (config.click) {
    el.addEventListener('click', el.awVueUploader.handleClick, false)
  }

  el.awVueUploader.fileInput.addEventListener('change', el.awVueUploader.handleFile, false)
}

const unmount = (el) => {
  if (el.awVueUploader) {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      el.removeEventListener(eventName, el.awVueUploader.preventDefaults, false)
    })

    ;['dragenter', 'dragover'].forEach(eventName => {
      el.removeEventListener(eventName, el.awVueUploader.highlight, false)
    })

    ;['dragleave', 'drop'].forEach(eventName => {
      el.removeEventListener(eventName, el.awVueUploader.unhighlight, false)
    })

    el.removeEventListener('drop', el.awVueUploader.handleDrop, false)

    if (el.awVueUploader.config.click) {
      el.removeEventListener('click', el.awVueUploader.handleClick, false)
    }
    
    el.awVueUploader.fileInput.removeEventListener('change', el.awVueUploader.handleFile, false)
    // remove old form
    el.awVueUploader.fileInput.parentNode.parentNode.removeChild(el.awVueUploader.fileInput.parentNode);

    delete el.awVueUploader
  }
}

export default {
  beforeMount (el, binding) {
    const config = { ...defaults, ...binding.value }
    el.awUploaderId = getBcUploaderStore().mountUploader(config)
    init(config, el)
  },
  updated (el, binding) {
    const currentConfig = getBcUploaderStore().getUploaderConfig(el.awUploaderId)
    const updatedConfig = { ...defaults, ...binding.value }

    const uploaderData = getBcUploaderStore().getUploaderData(el.awUploaderId)

    if (!uploaderData.isRunning) {
      getBcUploaderStore().resetUploaderFiles(el.awUploaderId)
    }

    // when uploader ID should be updated
    if ((currentConfig?.uploaderId && el.awUploaderId !== updatedConfig.uploaderId) || (!currentConfig?.uploaderId && updatedConfig.uploaderId)) {
      if (!uploaderData.isRunning) {
        getBcUploaderStore().unmountUploader(el.awUploaderId)
      }
      console.log('updating uploader ID', {from: el.awUploaderId, to: updatedConfig.uploaderId})
      
      el.awUploaderId = getBcUploaderStore().mountUploader(updatedConfig)
    } else {
      getBcUploaderStore().updateUploaderConfig(el.awUploaderId, updatedConfig)
    }

    unmount(el)
    init(updatedConfig, el)
  },
  unmounted (el) {
    console.log('unmounting uploader id', el.awUploaderId)

    const uploaderData = getBcUploaderStore().getUploaderData(el.awUploaderId)
    uploaderData.isMounted = false

    if (!uploaderData.isRunning) {
      getBcUploaderStore().resetUploaderFiles(el.awUploaderId)
      getBcUploaderStore().unmountUploader(el.awUploaderId)
    }

    unmount(el)
  }
}

/*

1) Na strance je pouzita direktiva bc-uploader="{ config }"
  a) beforeMount -> sloucime config s default hodnotami
  b) zaregistrujeme uploader do storu a priradime uploaderId
    a) Vygenerovani uploaderId
    b) Inicializace promise queue pro uploader
    c) Inicializace struktury reaktivnich dat -> data ktera se zobrazuji v uploadNotification
  c) zinicializujeme uploader -> pridame event listenery pro upload akce

2) Uzivatel pouzije uploader - pretahne na nej soubory, nebo vybere soubory po kliku -> metoda handleFiles
  a) Zkontrolujeme jestli jsou files validni
  b) Vsechny files zaregistrovat do store + pridat do uploaderQueue -> spusteni uploadu souboru pres uploadFile
  c) zinicializujeme toust notifikaci
  d) spustime queue pro dany uploader pokud jeste nebezi

3) Upload jednotliveho souboru -> kdyz na nej prijde rada v upladerQueue -> metoda uploadFile
  a) UploadFile zadefinuje a naplni metody start a pause
    a) start zavola api request na zahajeni uploadu souboru
    b) pause zavola api request na cancel uploadu souboru
  c) V uploader storu zahajime upload -> nastaveni uploaderData.currentFileIndex na index aktualniho souboru + naplneni seznamu parts + inicializace uploaderFileQueue
    a) v uploaderFileQueue sa spocita aktualny progress v api requeste
  d) uploadNotification, zobrazi aktualne nahravany subor a jeho poradie a progress

4) 

*/
